import { Controller } from "@hotwired/stimulus"

// Handles form behavior including client project toggles
export default class extends Controller {
  static targets = ["form", "generateButton"]

  submit() {
    this.formTarget.requestSubmit()
  }

  closeModal(event) {
    // Check if the submission was successful (no validation errors)
    const response = event.detail.fetchResponse
    if (response && response.succeeded) {
      // Find the closest modal dialog and close it
      const modal = this.element.closest('dialog.modal')
      if (modal) {
        modal.close()
      }
    }
  }

  autoResize(event) {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  toggleClientProjects(event) {
    const checkbox = event.target
    const clientId = checkbox.dataset.clientId
    const projectIds = checkbox.dataset.projectIds.split(',')
    
    // Find all project checkboxes for this client
    const projectCheckboxes = this.element.querySelectorAll(`input[type="checkbox"][data-client-id="${clientId}"]`)
    
    // Set all project checkboxes to match the client checkbox
    projectCheckboxes.forEach(projectCheckbox => {
      projectCheckbox.checked = checkbox.checked
    })
    
    // Update project dropdown display
    this.updateProjectDropdownDisplay()
  }

  toggleAllProjects(event) {
    const checked = event.target.checked
    
    // Find all project and client checkboxes
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"][name="project_ids[]"], input[type="checkbox"][name^="client_"]')
    
    // Set all checkboxes to match the select all checkbox
    checkboxes.forEach(checkbox => {
      checkbox.checked = checked
    })
    
    // Update project dropdown display
    this.updateProjectDropdownDisplay()
  }

  toggleRoleUsers(event) {
    const checkbox = event.target
    const role = checkbox.dataset.role
    const checked = checkbox.checked
    
    // Find all user checkboxes for this role
    const userCheckboxes = this.element.querySelectorAll(`input[type="checkbox"][name="user_ids[]"][data-role="${role}"]`)
    
    // Set all user checkboxes to match the role checkbox
    userCheckboxes.forEach(userCheckbox => {
      userCheckbox.checked = checked
    })
    
    // Update team dropdown display
    this.updateTeamDropdownDisplay()
  }

  toggleAllEntryTypes(event) {
    const checked = event.target.checked
    
    // Find all entry type checkboxes
    const entryTypeCheckboxes = this.element.querySelectorAll('input[type="checkbox"][name="entry_types[]"]')
    
    // Set all entry type checkboxes to match the select all checkbox
    entryTypeCheckboxes.forEach(checkbox => {
      checkbox.checked = checked
    })
    
    // Update entry types dropdown display
    this.updateEntryTypesDropdownDisplay()
  }

  toggleAllRoles(event) {
    const checked = event.target.checked
    
    // Find all role checkboxes
    const roleCheckboxes = this.element.querySelectorAll('input[type="checkbox"][name="roles[]"]')
    
    // Set all role checkboxes to match the select all checkbox
    roleCheckboxes.forEach(checkbox => {
      checkbox.checked = checked
    })
    
    // Update roles dropdown display
    this.updateRolesDropdownDisplay()
  }

  toggleInactiveProjects(event) {
    const showInactive = event.target.checked
    
    // Find all project items
    const projectItems = this.element.querySelectorAll('.project-item')
    
    projectItems.forEach(item => {
      const isActive = item.dataset.projectActive === 'true'
      
      if (isActive) {
        // Always show active projects
        item.style.display = ''
      } else {
        // Show/hide inactive projects based on checkbox
        item.style.display = showInactive ? '' : 'none'
      }
    })
    
    // Update project dropdown display after visibility changes
    this.updateProjectDropdownDisplay()
  }

  connect() {
    // Add event listeners for all project, team, and entry type checkboxes to update displays
    this.element.addEventListener('change', (event) => {
      if (event.target.name === 'project_ids[]') {
        this.updateProjectDropdownDisplay()
      } else if (event.target.name === 'user_ids[]') {
        this.updateTeamDropdownDisplay()
      } else if (event.target.name === 'entry_types[]') {
        this.updateEntryTypesDropdownDisplay()
      }
    })
    
    // Initialize generate button state on page load
    this.updateGenerateButton()
  }

  updateProjectDropdownDisplay() {
    const projectCheckboxes = this.element.querySelectorAll('input[name="project_ids[]"]:checked')
    const count = projectCheckboxes.length
    
    // Find the projects dropdown by looking for the dropdown that contains project checkboxes
    const dropdowns = this.element.querySelectorAll('.dropdown [role="button"]')
    let projectDropdown = null
    
    dropdowns.forEach(dropdown => {
      const dropdownContent = dropdown.nextElementSibling
      if (dropdownContent && dropdownContent.querySelector('input[name="project_ids[]"]')) {
        projectDropdown = dropdown
      }
    })
    
    if (projectDropdown) {
      const span = projectDropdown.querySelector('span')
      if (count > 0) {
        span.textContent = `${count} ${count === 1 ? 'project' : 'projects'} selected`
      } else {
        span.textContent = 'Select projects...'
      }
    }
  }

  updateTeamDropdownDisplay() {
    const teamCheckboxes = this.element.querySelectorAll('input[name="user_ids[]"]:checked')
    const count = teamCheckboxes.length
    
    // Find the team dropdown by looking for the dropdown that contains user checkboxes
    const dropdowns = this.element.querySelectorAll('.dropdown [role="button"]')
    let teamDropdown = null
    
    dropdowns.forEach(dropdown => {
      const dropdownContent = dropdown.nextElementSibling
      if (dropdownContent && dropdownContent.querySelector('input[name="user_ids[]"]')) {
        teamDropdown = dropdown
      }
    })
    
    if (teamDropdown) {
      const span = teamDropdown.querySelector('span')
      if (count > 0) {
        span.textContent = `${count} selected`
      } else {
        span.textContent = 'Select team members...'
      }
    }
  }

  updateEntryTypesDropdownDisplay() {
    const entryTypeCheckboxes = this.element.querySelectorAll('input[name="entry_types[]"]:checked')
    const count = entryTypeCheckboxes.length
    
    // Find the entry types dropdown by looking for the dropdown that contains entry type checkboxes
    const dropdowns = this.element.querySelectorAll('.dropdown [role="button"]')
    let entryTypesDropdown = null
    
    dropdowns.forEach(dropdown => {
      const dropdownContent = dropdown.nextElementSibling
      if (dropdownContent && dropdownContent.querySelector('input[name="entry_types[]"]')) {
        entryTypesDropdown = dropdown
      }
    })
    
    if (entryTypesDropdown) {
      const span = entryTypesDropdown.querySelector('span')
      if (count > 0) {
        const typeText = count === 1 ? 'type' : 'types'
        span.textContent = `${count} ${typeText} selected`
      } else {
        span.textContent = 'Select types...'
      }
    }
  }

  updateRolesDropdownDisplay() {
    const roleCheckboxes = this.element.querySelectorAll('input[name="roles[]"]:checked')
    const count = roleCheckboxes.length
    
    // Find the roles dropdown by looking for the dropdown that contains role checkboxes
    const dropdowns = this.element.querySelectorAll('.dropdown [role="button"]')
    let rolesDropdown = null
    
    dropdowns.forEach(dropdown => {
      const dropdownContent = dropdown.nextElementSibling
      if (dropdownContent && dropdownContent.querySelector('input[name="roles[]"]')) {
        rolesDropdown = dropdown
      }
    })
    
    if (rolesDropdown) {
      const span = rolesDropdown.querySelector('span')
      if (count > 0) {
        const roleText = count === 1 ? 'role' : 'roles'
        span.textContent = `${count} ${roleText} selected`
      } else {
        span.textContent = 'All roles'
      }
    }
  }

  handleResponse(event) {
    const [data, status, xhr] = event.detail
    if (status === 'OK') {
      // Get the task ID from the form's action URL
      const taskId = this.element.action.match(/tasks\/(\d+)/)[1]
      
      // Find the notes row
      const notesRow = document.getElementById(`task_notes_${taskId}`)
      if (notesRow) {
        // Get the notes content from the textarea
        const notes = this.element.querySelector('textarea[name="milestone_task[notes]"]').value
        
        // Get or create the display container
        let displayContainer = notesRow.querySelector('.flex.items-center.group')
        if (!displayContainer) {
          displayContainer = document.createElement('div')
          displayContainer.className = 'flex items-center justify-between group'
          notesRow.querySelector('[data-controller="inline-edit"]').prepend(displayContainer)
        }

        // Get or create the notes display div
        let displayDiv = displayContainer.querySelector('.flex-grow')
        if (!displayDiv) {
          displayDiv = document.createElement('div')
          displayDiv.className = 'flex-grow'
          displayContainer.prepend(displayDiv)
        }

        // Update the content
        displayDiv.textContent = notes
        
        // Show the display container and hide the form
        displayContainer.classList.remove('hidden')
        this.element.parentElement.classList.add('hidden')
        
        // Show the notes row if it was hidden
        notesRow.classList.remove('hidden')
      }
    }
  }

  updateGenerateButton() {
    // Check if a team member is selected
    const selectedMember = this.element.querySelector('input[name="user_id"]:checked')
    
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = !selectedMember
    }
    
    // Update dropdown display text
    const dropdown = this.element.querySelector('.dropdown [role="button"] span')
    if (dropdown && selectedMember) {
      const memberLabel = selectedMember.closest('label')
      const memberName = memberLabel.querySelector('.font-medium').textContent
      dropdown.textContent = memberName
    }
  }
} 